<script>import { createEventDispatcher } from "svelte";
export let cells = [];
export let columns = 1;
export let itemCharWidth = 2;
export let itemCharHeight = 1;
export let cellCharWidth = 3;
export let cellContentPadding = "0.2em";
export let cellPadding = "0.3em";
export let makeSquare = false;
const dispatch = createEventDispatcher();
let rows = cells.length > 0 && columns > 0 ? Math.ceil(cells.length / columns) : 0;
let cellAspectRatio = makeSquare && rows > 0 && columns > 0 ? rows / columns : 1;
$: {
  rows = cells.length > 0 && columns > 0 ? Math.ceil(cells.length / columns) : 0;
  cellAspectRatio = makeSquare && rows > 0 && columns > 0 ? rows / columns : 1;
}
const cellClick = (ev) => {
  const targetNode = ev.target;
  const cellNode = targetNode.closest(".cell");
  if (cellNode && cellNode.dataset.cell) {
    const index = parseInt(cellNode.dataset.cell);
    const cell = cells[parseInt(cellNode.dataset.cell)];
    if (cell) {
      dispatch("cell-click", { cell, index });
    }
  }
};
</script>

<div
	class="picker"
	style={`--columns:${columns}; --rows:${rows}; --cell-aspect-ratio:${cellAspectRatio}; --item-char-width:${itemCharWidth}; --item-char-height:${itemCharHeight}; --cell-char-width:${cellCharWidth}; --cell-content-padding:${cellContentPadding}; --cell-padding:${cellPadding};`}
>
	<div class="picker-cells border">
		{#each cells as cell, index}
			<div
				on:click={cellClick}
				on:keypress={cellClick}
				role="button"
				tabindex="0"
				class="cell i{index} {cell.type} {cell.valueTop ? 'top' : ''} {cell.valueLeft
					? 'left'
					: ''} {cell.marked ? 'marked' : ''} {cell.inside ? 'inside' : ''} {cell.selected
					? 'selected'
					: ''}  {cell.disabled ? 'disabled' : ''}"
				data-cell={index}
			>
				<div>{@html cell.label}</div>
			</div>
		{/each}
	</div>
</div>

<style>
	div.picker {
		--base-color-onSurface-primary: #dbe4e7;
		--base-color-onSurface-tertiary: #46464a;
		--base-color-primary-hover: #4b765b;
		--base-color-surface-default: #1a1b1f;
		--base-color-bg-default: #2d2f36;
		--base-color-primary-default: #6bae85;
		--font-family: Roboto;
		--font-size: 1.25rem;
		--font-size-small: 0.6875rem;
		--font-weight: 700;
		--xletter-spacing: 0.0625rem;
		--letter-spacing: 0;

		--cell-border-width: 0.0625rem;
	}

	div.picker {
		--picker-font: normal var(--font-weight) var(--font-size) / 1 var(--font-family);
		--picker-font-spacing: var(--letter-spacing);

		--fill: var(--base-color-surface-default);

		--cell-on-surface: var(--base-color-onSurface-primary);
		--cell-secondary-on-surface: var(--base-color-onSurface-tertiary);

		--cell-marked-border: var(--base-color-primary-hover);

		--cell-disabled-on-surface: var(--fill);
		--cell-disabled-surface: var(--base-color-bg-default);

		--cell-selected-on-surface: var(--fill);
		--cell-selected-surface: var(--base-color-primary-default);

		--cell-inside-selection-surface: var(--base-color-onSurface-tertiary);
		--cell-inside-selection-on-surface: var(--base-color-onSurface-primary);

		--cell-border-color: var(--base-color-onSurface-primary);

		--v-size-gain: max(1, calc(1 / var(--cell-aspect-ratio)));

		--cell-content-width: calc(1em * 0.7 * var(--item-char-width));
		--cell-content-height: calc(1em * var(--item-char-height));
		--cell-content-size: max(var(--cell-content-width), var(--cell-content-height));
		--cell-min-size: calc(1em * 0.8 * var(--cell-char-width));
		--cell-height: calc(
			var(--v-size-gain) *
				max(
					var(--cell-min-size),
					calc(
						var(--cell-content-size) + (var(--cell-content-padding) * 2) + (var(--cell-padding) * 2) +
							var(--cell-border-width)
					)
				)
		);
	}

	div.picker {
		display: block;
		background-color: var(--fill);
		width: calc(var(--columns) * var(--cell-height) * var(--cell-aspect-ratio));
		height: calc(var(--rows) * var(--cell-height));
		min-width: calc(var(--columns) * var(--cell-height) * min(1, var(--cell-aspect-ratio)));
		min-height: calc(var(--rows) * var(--cell-height));
		-webkit-user-select: none;
		user-select: none;
		font: var(--picker-font);
		letter-spacing: var(--picker-font-spacing);
	}

	div.picker-cells {
		color: var(--cell-on-surface);
	}
	div.picker-cells > div.cell.secondary {
		color: var(--cell-secondary-on-surface);
	}
	div.picker-cells > div.cell.disabled {
		color: var(--cell-disabled-on-surface);
		background-color: var(--cell-disabled-surface);
	}
	div.picker-cells > div.cell.selected:not(.disabled) {
		color: var(--cell-selected-on-surface);
		background-color: var(--cell-selected-surface);
	}
	div.picker-cells > div.cell.inside:not(.disabled, .selected) {
		color: var(--cell-inside-selection-on-surface);
		background-color: var(--cell-inside-selection-surface);
	}
	div.picker-cells > div.cell.marked:not(.disabled, .selected, .inside) > div {
		border-color: var(--cell-marked-border);
	}

	div.picker-cells {
		display: grid;
		grid-template-columns: repeat(var(--columns), 1fr);
		grid-template-rows: repeat(var(--rows), 1fr);
		justify-items: stretch;
		align-items: stretch;
		width: 100%;
		height: 100%;
	}

	div.picker-cells > div.cell {
		text-align: center;
		font: var(--picker-font);
		letter-spacing: var(--picker-font-spacing);
	}

	div.picker-cells > div.cell {
		cursor: default;
	}
	div.picker-cells > div.cell.primary:not(.disabled),
	div.picker-cells > div.cell.secondary:not(.disabled) {
		cursor: pointer;
	}

	div.picker-cells > div.cell.spacer,
	div.picker-cells > div.cell.primary,
	div.picker-cells > div.cell.secondary {
		border-right: var(--cell-border-width) solid transparent;
		border-bottom: var(--cell-border-width) solid transparent;
	}
	div.picker-cells > div.cell.top {
		border-top: var(--cell-border-width) solid transparent;
	}
	div.picker-cells > div.cell.left {
		border-left: var(--cell-border-width) solid transparent;
	}
	div.picker-cells.border > div.cell.spacer,
	div.picker-cells.border > div.cell.primary,
	div.picker-cells.border > div.cell.secondary {
		border-color: var(--cell-border-color);
	}
	div.picker-cells > div.cell > div {
		display: inline-block;
		width: calc(var(--cell-content-size) + (var(--cell-content-padding) * 2) + 2px);
		height: calc(var(--cell-content-size) + (var(--cell-content-padding) * 2));
		padding: calc(
				var(--cell-content-padding) +
					calc((var(--cell-content-size) - var(--cell-content-height)) / 2)
			)
			calc(
				var(--cell-content-padding) +
					calc((var(--cell-content-size) - var(--cell-content-width)) / 2)
			);
		margin-top: calc(
			(
					var(--cell-height) - var(--cell-border-width) * 1 - var(--cell-content-size) -
						(var(--cell-content-padding) * 2)
				) * 0.5
		);
		text-align: center;
		border: 1px solid transparent;
		border-radius: 50%;
	}
</style>
